/**
 * DateShake Registration Component
 * Copyright (c) 2024 DateShake. All rights reserved.
 * 
 * Multi-step registration form with validation, legal agreements, and security measures
 */

import { FormEvent, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Api } from '../lib/apiClient'
import LegalAgreements from '../components/LegalAgreements'
import Captcha from '../components/Captcha'
import OnboardingTour from '../components/OnboardingTour'
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator'
import { validateRegistrationForm, type FormData } from '../lib/formValidation'

type Step = 1 | 2 | 3 | 4

export function RegisterForm({ onSuccess }: { onSuccess?: () => void }) {
	return <RegisterWizard onSuccess={onSuccess} />
}

function RegisterWizard({ onSuccess }: { onSuccess?: () => void }) {
	const { login } = useAuth()
	const navigate = useNavigate()
	const [step, setStep] = useState<Step>(1)
	const [error, setError] = useState<string | null>(null)
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

	// Step 1 fields
	const [firstName, setFirstName] = useState('')
	const [lastName, setLastName] = useState('')
	const [birthday, setBirthday] = useState('')
	const [zip, setZip] = useState('')
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [confirm, setConfirm] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)

	// Step 2 fields
	const [dietRestrictions, setDietRestrictions] = useState<string[]>([])
	const [cuisines, setCuisines] = useState<string[]>([])
	const [entertainment, setEntertainment] = useState<string[]>([])
	const [hobbies, setHobbies] = useState<string[]>([])

	// Step 3 fields
	const [legalAccepted, setLegalAccepted] = useState(false)
	const [captchaVerified, setCaptchaVerified] = useState(false)

	const maxDate = useMemo(() => {
		const d = new Date()
		d.setFullYear(d.getFullYear() - 13) // Changed to 13 for age validation
		return d.toISOString().split('T')[0]
	}, [])

	const minDate = useMemo(() => {
		const d = new Date()
		d.setFullYear(d.getFullYear() - 125)
		return d.toISOString().split('T')[0]
	}, [])


	const validateAge = (birthday: string) => {
		if (!birthday) return true
		
		const birthDate = new Date(birthday)
		const today = new Date()
		const age = today.getFullYear() - birthDate.getFullYear()
		const monthDiff = today.getMonth() - birthDate.getMonth()
		
		if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
			return age - 1 >= 13
		}
		return age >= 13
	}

	const next = () => {
		// Validate current step before proceeding
		if (step === 1) {
			const formData: FormData = {
				firstName,
				lastName,
				email,
				password,
				confirmPassword: confirm,
				birthday,
				zip
			}
			const validation = validateRegistrationForm(formData)
			if (!validation.isValid) {
				setValidationErrors(validation.errors)
				return
			}
			setValidationErrors({})
		}
		setStep(s => (s === 4 ? 4 : ((s + 1) as Step)))
	}
	const back = () => setStep(s => (s === 1 ? 1 : ((s - 1) as Step)))

	const onSubmit = async (e: FormEvent) => {
		e.preventDefault()
		setError(null)
		if (step !== 4) return
		
		// Final validation
		if (!legalAccepted) {
			setError('You must accept the Terms of Service and Privacy Policy to continue')
			return
		}
		
		if (!captchaVerified) {
			setError('Please complete the security verification')
			return
		}
		try {
			console.log('Starting registration with API client')
			const data = await Api.register({
				email,
				name: `${firstName} ${lastName}`.trim(),
				password,
				profile: { firstName, lastName, birthday, zip },
				preferences: { dietRestrictions, cuisines, entertainment, hobbies },
				legalAgreements: {
					termsAccepted: legalAccepted,
					privacyAccepted: legalAccepted
				}
			})
			console.log('Registration successful:', data)
			
			// Validate response data
			if (!data.token || !data.user) {
				console.error('Invalid registration response - missing token or user:', data)
				throw new Error('Invalid response from server')
			}
			
			if (!data.user.email || !data.user.name || !data.user.id) {
				console.error('Invalid user data in response:', data.user)
				throw new Error('Invalid user data received')
			}
			
			// Login the user immediately
			console.log('Attempting to login user:', data.user)
			try {
				login(data.token, data.user)
			} catch (loginError) {
				console.error('Login failed after registration:', loginError)
				const errorMessage = loginError instanceof Error ? loginError.message : 'Unknown login error'
				throw new Error('Failed to log in after registration: ' + errorMessage)
			}
			
			// Close modal first
			onSuccess?.()
			
			// Navigate to dashboard after a brief delay to ensure state is updated
			setTimeout(() => {
				navigate('/dashboard')
			}, 200)
		} catch (err: any) {
			console.error('Registration error:', err)
			setError(err.message)
		}
	}

	return (
		<form className="space-y-4" onSubmit={onSubmit}>
			{error && <div className="text-red-600 text-sm">{error}</div>}
			{step === 1 && (
				<div className="space-y-4">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								First Name <span className="text-red-500">*</span>
							</label>
							<input 
								className={`w-full border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.firstName ? 'border-red-500' : 'border-gray-300'}`} 
								placeholder="First Name" 
								value={firstName} 
								onChange={e => setFirstName(e.target.value)} 
							/>
							{validationErrors.firstName && <p className="text-red-500 text-sm mt-1">{validationErrors.firstName}</p>}
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Last Name <span className="text-red-500">*</span>
							</label>
							<input 
								className={`w-full border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.lastName ? 'border-red-500' : 'border-gray-300'}`} 
								placeholder="Last Name" 
								value={lastName} 
								onChange={e => setLastName(e.target.value)} 
							/>
							{validationErrors.lastName && <p className="text-red-500 text-sm mt-1">{validationErrors.lastName}</p>}
						</div>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Birthday <span className="text-red-500">*</span>
							</label>
							<input 
								type="date" 
								className={`w-full border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.birthday ? 'border-red-500' : 'border-gray-300'}`} 
								min={minDate} 
								max={maxDate} 
								value={birthday} 
								onChange={e => {
									setBirthday(e.target.value)
									if (e.target.value && !validateAge(e.target.value)) {
										setValidationErrors(prev => ({ ...prev, birthday: 'You must be at least 13 years old to register' }))
									} else {
										setValidationErrors(prev => ({ ...prev, birthday: '' }))
									}
								}} 
							/>
							{validationErrors.birthday && <p className="text-red-500 text-sm mt-1">{validationErrors.birthday}</p>}
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Zipcode <span className="text-red-500">*</span>
							</label>
							<input 
								className={`w-full border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.zip ? 'border-red-500' : 'border-gray-300'}`} 
								placeholder="Zipcode" 
								value={zip} 
								onChange={e => setZip(e.target.value)} 
							/>
							{validationErrors.zip && <p className="text-red-500 text-sm mt-1">{validationErrors.zip}</p>}
						</div>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Email Address <span className="text-red-500">*</span>
						</label>
						<input 
							className={`w-full border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.email ? 'border-red-500' : 'border-gray-300'}`} 
							placeholder="Email Address" 
							type="email"
							value={email} 
							onChange={e => setEmail(e.target.value)} 
						/>
						{validationErrors.email && <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>}
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Password <span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input 
								className={`w-full border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.password ? 'border-red-500' : 'border-gray-300'}`} 
								placeholder="Password" 
								type={showPassword ? "text" : "password"} 
								value={password} 
								onChange={e => setPassword(e.target.value)} 
							/>
							<button
								type="button"
								className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
								onClick={() => setShowPassword(!showPassword)}
							>
								{showPassword ? '🙈' : '👁️'}
							</button>
						</div>
						{validationErrors.password && <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>}
						<PasswordStrengthIndicator password={password} />
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Confirm Password <span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input 
								className={`w-full border p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validationErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`} 
								placeholder="Confirm Password" 
								type={showConfirmPassword ? "text" : "password"} 
								value={confirm} 
								onChange={e => setConfirm(e.target.value)} 
							/>
							<button
								type="button"
								className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
								onClick={() => setShowConfirmPassword(!showConfirmPassword)}
							>
								{showConfirmPassword ? '🙈' : '👁️'}
							</button>
						</div>
						{validationErrors.confirmPassword && <p className="text-red-500 text-sm mt-1">{validationErrors.confirmPassword}</p>}
					</div>
					<div className="flex justify-end">
						<button type="button" className="btn-primary px-8 py-3" onClick={next}>Next</button>
					</div>
				</div>
			)}

			{step === 2 && (
				<div className="space-y-6 max-h-[70vh] overflow-y-auto">
					<div className="text-center">
						<h3 className="text-lg font-semibold text-black mb-2">Tell us about your preferences</h3>
						<p className="text-gray-600 text-sm">This helps us personalize your date recommendations</p>
					</div>
					<div className="space-y-6">
						<PreferenceMulti 
							label="Diet Restrictions" 
							values={dietRestrictions} 
							setValues={setDietRestrictions} 
							options={["Vegetarian","Vegan","Gluten-Free","Halal","Kosher"]} 
							moreOptions={["Paleo","Keto","Low-Carb","Dairy-Free","Nut-Free","Soy-Free"]}
						/>
						<PreferenceMulti 
							label="Cuisine Preferences" 
							values={cuisines} 
							setValues={setCuisines} 
							options={["Italian","Mexican","Thai","Japanese","American","Mediterranean"]} 
							moreOptions={["Chinese","Indian","French","Korean","Vietnamese","Greek","Spanish","Brazilian","Ethiopian","Lebanese"]}
						/>
						<PreferenceMulti 
							label="Entertainment Interests" 
							values={entertainment} 
							setValues={setEntertainment} 
							options={["Movies","Live Music","Theatre","Comedy","Karaoke"]} 
							moreOptions={["Concerts","Museums","Art Galleries","Dance","Sports","Gaming","Books","Podcasts","Stand-up","Magic Shows"]}
						/>
						<PreferenceMulti 
							label="Hobbies/Interests" 
							values={hobbies} 
							setValues={setHobbies} 
							options={["Hiking","Gaming","Reading","Cooking","Travel"]} 
							moreOptions={["Photography","Painting","Gardening","Yoga","Swimming","Cycling","Dancing","Music","Writing","Volunteering"]}
						/>
					</div>
					<div className="flex justify-between gap-3 pt-4 sticky bottom-0 bg-white">
						<button type="button" className="btn-nav px-6 py-2" onClick={back}>Back</button>
						<div className="flex gap-3">
							<button type="button" className="btn-nav px-6 py-2" onClick={next}>Skip</button>
							<button type="button" className="btn-primary px-6 py-2" onClick={next}>Next</button>
						</div>
					</div>
				</div>
			)}

			{step === 3 && (
				<div className="space-y-6">
					<OnboardingTour 
						onComplete={next}
						onSkip={next}
					/>
				</div>
			)}

			{step === 4 && (
				<div className="space-y-6">
					<div className="text-center">
						<h3 className="text-lg font-semibold text-black mb-2">Almost there!</h3>
						<p className="text-gray-600 text-sm">Just a few final steps to complete your registration</p>
					</div>
					
					<Captcha onVerify={setCaptchaVerified} />
					
					<LegalAgreements onAccept={setLegalAccepted} includeAgeVerification={false} />
					
					<div className="flex justify-between gap-3 pt-4">
						<button type="button" className="btn-nav px-6 py-2" onClick={back}>Back</button>
						<button 
							type="submit" 
							className="btn-primary px-8 py-3"
							disabled={!legalAccepted || !captchaVerified}
						>
							Create Account
						</button>
					</div>
				</div>
			)}
		</form>
	)
}

type PreferenceMultiProps = { label: string; values: string[]; setValues: (v: string[]) => void; options: string[]; moreOptions?: string[] }
function PreferenceMulti({ label, values, setValues, options, moreOptions = [] }: PreferenceMultiProps) {
	const [showMore, setShowMore] = useState(false)
	const [showNone, setShowNone] = useState(false)
	
	const toggle = (opt: string) => {
		if (opt === 'None') {
			setShowNone(!showNone)
			if (!showNone) {
				setValues(['None'])
			} else {
				setValues([])
			}
		} else {
			// If selecting any option other than None, remove None from selection
			const newValues = values.includes(opt) 
				? values.filter(v => v !== opt)
				: [...values.filter(v => v !== 'None'), opt]
			setValues(newValues)
			if (newValues.length > 0) {
				setShowNone(false)
			}
		}
	}
	
	const allOptions = showMore ? [...options, ...moreOptions] : options
	
	return (
		<div>
			<p className="text-black font-medium mb-2">{label}</p>
			<div className="flex flex-wrap gap-2">
				<button 
					type="button" 
					onClick={() => toggle('None')} 
					className={`chip ${showNone ? 'chip-selected' : ''}`}
				>
					None
				</button>
				{allOptions.map(o => (
					<button key={o} type="button" onClick={() => toggle(o)} className={`chip ${values.includes(o) ? 'chip-selected' : ''}`}>{o}</button>
				))}
				{moreOptions.length > 0 && !showMore && (
					<button 
						type="button" 
						onClick={() => setShowMore(true)} 
						className="chip border-dashed border-2 border-gray-300 text-gray-600 hover:border-gray-400"
					>
						More...
					</button>
				)}
			</div>
		</div>
	)
}

export default function Register() {
	return (
		<section className="max-w-md mx-auto px-4">
			<h1 className="text-2xl font-semibold text-black">Register</h1>
			<div className="mt-4">
				<RegisterForm />
			</div>
		</section>
	)
}
